// hooks/useInvoiceCalculations.ts
import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from './useRedux';
import { calculateInvoice, clearInvoiceCalculationData } from '../features/invoice/invoice.slice';
import { InvoiceCalculationsPayload } from '../features/invoice/invoice.service';
import { getSeparatedUserCalculationsTotal } from '../utils/invoiceTransformers';

/**
 * Custom hook for invoice calculations
 * 
 * Usage example:
 * 
 * const { calculate, calculateMultipleUsers, loading, calculationData, error, clearData } = useInvoiceCalculations();
 * 
 * // For single user
 * const result = await calculate(payload);
 * 
 * // For multiple users (collective invoice)
 * const { calculations, total } = await calculateMultipleUsers(invoiceHours, otherArticlesData, params);
 */
export const useInvoiceCalculations = () => {
  const dispatch = useAppDispatch();
  const invoiceState = useAppSelector((state) => state?.invoice);
  const { calculationData, loading, error } = invoiceState || {  
    calculationData: null, 
    loading: false, 
    error: null 
  };

  /**
   * Calculate invoice for a single user
   */
  const calculate = useCallback(async (payload: InvoiceCalculationsPayload) => {
    try {
      const result = await dispatch(calculateInvoice(payload));
      
      if (calculateInvoice.fulfilled.match(result)) {
        // Return the response data
        return result.payload?.response || result.payload?.data || result.payload;
      } else {
        throw new Error('Failed to calculate invoice');
      }
    } catch (error) {
      throw error;
    }
  }, [dispatch]);

  /**
   * Calculate invoice for multiple users (collective invoice)
   * This matches the logic from fetchInvoiceCalculationsUtil
   */
  const calculateMultipleUsers = useCallback(async (
    invoiceHours: any[],
    otherArticlesData: any[],
    params: {
      vat: number;
      currency: string;
      currencyGlobalValue: number;
      globalData: any;
      pension?: any;
      pensionAmount?: any;
      holidaySavings?: any;
      holidaySavingFee?: any;
      rutDeduction: boolean;
      rootDeduction: boolean;
      expressPayout: boolean;
      expenditureSum: number;
      allowancePerDaySweden: { allowance_in: number; allowance_out: number };
      anyArrayHasValue: boolean;
      selectedCustomer: any;
      customer: any;
      isPreliminaryInvoice?: boolean;
      prefillingData?: any;
    }
  ) => {
    try {
      if (!invoiceHours && !params.anyArrayHasValue) {
        return { calculations: [], total: null };
      }

      // Import the payload builder
      const { buildInvoiceCalculationPayloadForUser } = await import('../utils/invoiceTransformers');

      // Calculate for each user
      const calculations = await Promise.all(
        (invoiceHours || []).map(async (userInvoiceHours: any, userIndex: number) => {
          const userOtherArticles = otherArticlesData[userIndex] || [];

          // Build payload for this user
          const payload = buildInvoiceCalculationPayloadForUser(
            userInvoiceHours,
            userOtherArticles,
            userIndex,
            params
          );

          // Update number of freelancers
          payload.numberOfFreelancers = invoiceHours.length;

          // Call API for this user
          const result = await calculate(payload);
          return result;
        })
      );

      // Calculate total across all users
      const total = getSeparatedUserCalculationsTotal(calculations);

      return {
        calculations,
        total,
      };
    } catch (error) {
      console.error('Error in calculateMultipleUsers:', error);
      throw error;
    }
  }, [calculate]);

  const clearData = useCallback(() => {
    dispatch(clearInvoiceCalculationData());
  }, [dispatch]);

  return {
    calculate,
    calculateMultipleUsers,
    loading,
    calculationData,
    error,
    clearData,
  };
};
